import { DataTableWithActions } from '../DataTableWithActions';
import { useDataManagementContext } from '../../../hooks/useDataManagementContext';
import type { DataManagementAction } from '../../../interfaces/DataManagementAction';
import type { FieldValues } from 'react-hook-form';
import { useRelatedDataWithContext } from '../../../hooks/useRelatedDataWithContext';
import { Logger } from '@shared/logger';

interface DataManagementTableProps<TData extends FieldValues> {
  actions?: DataManagementAction<TData>[];
}

export function DataManagementTable<TData extends FieldValues>({ actions }: DataManagementTableProps<TData>) {
  const { 
    data, 
    isLoading, 
    mappings, 
    permissions,
    onSelectItem, 
    handleDelete,
    onOpenChange,
    isDeleting 
  } = useDataManagementContext<TData>();
  const logger = new Logger();

  const canUpdate = permissions.verbs?.update;
  const canDelete = permissions.verbs?.delete;

  // Use the generic related data hook
  const { relatedData, isLoading: relatedDataLoading, error: relatedDataError } = useRelatedDataWithContext(mappings);

  // Show error if related data failed to load
  if (relatedDataError) {
    logger.warn('Related data loading error', 'data.users', undefined, { error: relatedDataError });
  }

  return (
    <DataTableWithActions<TData & { id: string }>
      data={data as (TData & { id: string })[]}
      isLoading={isLoading || relatedDataLoading}
      mappings={mappings}
      onEdit={canUpdate ? (item) => {
        onSelectItem(item as TData);
        onOpenChange(true);
      } : undefined}
      onDelete={canDelete ? handleDelete : undefined}
      isDeleting={isDeleting}
      customActions={actions as DataManagementAction<TData & { id: string }>[] | undefined}
      relatedData={relatedData}
    />
  );
} 